import React, {useEffect, useState} from 'react';
import {Button, Col, Input, message, Popconfirm, Row, Table} from 'antd';
import {
    SystemDto,
    TestPlanSchedulerControllerApi, TestPlanTaskResponse
} from "../api";

import {useKeycloakHeader} from "../config/Config";
import {useKeycloak} from "@react-keycloak/web";
import {hasRole, ROLES} from "../components/misc/RolesHelper";
import MissingAuthorizationPage from "./error/MissingAuthorizationPage";
import {parseCronExpression} from "../utils/CronExpression";
import CronModal from "../components/modal/scheduling/CronModal";
import {DeleteOutlined, HistoryOutlined, PlusOutlined} from "@ant-design/icons";
import TestPlanExecutionModal from "../components/modal/testExecution/TestPlanExecutionModal";
import {formatDateTime} from "../utils/Utils";


const SchedulingOverview = () => {
    const {keycloak} = useKeycloak();
    const headers = useKeycloakHeader();
    const testPlanSchedulerControllerApi = new TestPlanSchedulerControllerApi();

    // State für API-Daten und Ladezustand
    const [data, setData] = useState<TestPlanTaskResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [isCronModalVisible, setIsCronModalVisible] = useState(false);

    const [selectedTestPlanId, setSelectedTestPlanId] = useState<number | null>(null);
    const [testPlanExecutionVisible, setTestPlanExecutionVisible] = useState(false);

    // Definiere die Spalten der Tabelle
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Name',
            dataIndex: ['testPlan', 'name'],
            key: 'testplan.name',
        },
        {
            title: 'Zeitpunkt',
            dataIndex: 'cronExpression',
            key: 'cronExpression',
            render: (cronExpression) => {
                try {
                    // Nutze parseCronExpression, um die Cron-Expression in lesbaren Text zu konvertieren
                    return parseCronExpression(cronExpression);
                } catch (error) {
                    console.error('Ungültige Cron-Expression:', cronExpression);
                    return 'Ungültige Zeitangabe';
                }
            },
        },
        {
            title: 'Ausführungen',
            dataIndex: 'execution',
            key: 'execution',
            width: '5%',
            render: (text, record: TestPlanTaskResponse) => (
                <Button danger onClick={() => {
                    if(record.testPlan && record.testPlan.id){
                        setSelectedTestPlanId(record.testPlan?.id);
                        setTestPlanExecutionVisible(true);
                    }
                }} style={{marginRight: '8px', border: '0px', color: '#1890ff'}}>
                    <HistoryOutlined/>
                </Button>
            ),
        },
        {
            title: 'Aktionen',
            key: 'actions',
            width: '5%',
            render: (_: any, record: SystemDto) => (
                <>
                    <Popconfirm
                        title="Willst du diese Zeitplanung wirklich löschen?"
                        onConfirm={() => record.id ? handleDeleteTask(record.id)
                            : message.error("System nicht gefunden!")}
                        okText="Ja"
                        cancelText="Nein"
                    >
                        <Button danger>
                            <DeleteOutlined/>
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
        {
            title: 'Erstellt am',
            dataIndex: 'creationTs',
            key: 'creationTs',
        }
    ];

    // Löscht eine Task
    const handleDeleteTask = (id: number) => {
        testPlanSchedulerControllerApi.deleteScheduledTask(id, headers).then((r) => {
            message.success("Testplan wurde erfolgreich gelöscht!");
            fetchData();
        }).catch((error) => {
            message.error({content: "Es ist ein unerwarteter Fehler aufgetreten! ", duration: 30});
        })
    };

    // Filtert die Daten basierend auf dem Suchtext
    const filteredData = data.filter(item =>
        item.testPlan?.name?.toLowerCase().includes(searchText.toLowerCase())
    );

    // Daten abrufen beim ersten Render
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = () => {
        setLoading(true);
        // Hier kann ein PageableObject erstellt werden, um Paging zu unterstützen
        const pageable = {page: 0, size: 10}; // Beispiel für Paging
        testPlanSchedulerControllerApi.getScheduledTasks(pageable, headers)
            .then((axiosResponse) => {
                const responseData = axiosResponse.data;
                if(responseData.content){
                    const formattedDate = responseData.content.map((reponse: TestPlanTaskResponse)=>({
                        ...reponse,
                        creationTs: formatDateTime(reponse.creationTs)
                    }))
                    setData(formattedDate);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('Fehler beim Abrufen der Daten:', error);
                setLoading(false);
            });
    };

    let roles = [
        ROLES.GLOW_ADMIN,
        ROLES.GLOW_TEST_MANAGER,
        ROLES.GLOW_TEST_DEVELOPER,
        ROLES.GLOW_TEST_DESIGNER
    ];
    const hasRoleForPage = hasRole(roles, keycloak);
    if (!hasRoleForPage) {
        return (<MissingAuthorizationPage roles={roles}/>);
    }

    return (
        <div>
            <h1>Zeitplanung</h1>
            <Row>
                {hasRole([ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_DESIGNER], keycloak) &&
                    <Col flex={"70px"}>
                      <Button type="primary" onClick={() => setIsCronModalVisible(true)}>
                        <PlusOutlined/>
                      </Button>
                      <CronModal
                          isOpen={isCronModalVisible}
                          onClose={() => setIsCronModalVisible(false)}
                          onSave={() => fetchData()}
                      />
                    </Col>}
                <Col flex={"auto"}>
                    <Input
                        placeholder="Nach Namen suchen"
                        onChange={(e) => setSearchText(e.target.value)}
                        value={searchText}
                    />
                </Col>
            </Row>
            <Table
                dataSource={filteredData}
                columns={columns}
                loading={loading}
                rowKey="id" // Hier sollte der eindeutige Schlüssel des Datensatzes verwendet werden
                locale={{emptyText: 'Keine Daten vorhanden'}}
            />
            {selectedTestPlanId &&
                <TestPlanExecutionModal testPlanId={selectedTestPlanId} visible={testPlanExecutionVisible}
                                        onClose={() => setTestPlanExecutionVisible(false)}/>}
        </div>
    );
};

export default SchedulingOverview;